<template>
    <div>
        <report-header
            :title="title"
            :isLoading="isLoading"
            :reportPagination="pagination"
            :isShowDateRange="true"
            :formatDateRangeTitle="dateRange"
        />

        <b-card id="report-card">
            <div v-if="!loaded">
                <p class="mt-3">
                    Please select the
                    <a @click="handleOpenFilter" class="text-link">filter</a>
                </p>
            </div>
            <div id="report-body" v-if="loaded" class="w-100 mb-4">
                <div v-if="grandTotal > 0">
                    <h4 class="mt-3">Summary</h4>
                    <QTable
                        id="active-engaged-patient-summary-report"
                        :data="summaryResults"
                        :loading="isLoading"
                        :columns="summaryColumns"
                        :rows-per-page-options="[0]"
                        :hide-bottom="true"
                        row-key="category"
                    >
                        <template v-slot:body="props">
                            <q-tr :props="props">
                                <q-td
                                    v-if="!shouldExpand(props.row)"
                                    :props="props"
                                    key="category"
                                    :class="props.row.class"
                                >
                                    {{ props.row.category }}
                                </q-td>
                                <q-td v-else :props="props" key="category">
                                    <div
                                        @click="toggleExpandAll(props)"
                                        class="pr-2 text-link"
                                        :class="props.row.class"
                                    >
                                        {{ props.row.category }}
                                    </div>
                                </q-td>
                                <q-td
                                    v-if="!shouldExpand(props.row)"
                                    :props="props"
                                    key="total"
                                    :class="props.row.class"
                                >
                                    {{ props.row.total }}
                                </q-td>
                                <q-td
                                    v-else
                                    :props="props"
                                    key="total"
                                    :class="props.row.class"
                                >
                                    {{ props.expand ? "" : otherSpeciesTotal }}
                                </q-td>
                            </q-tr>
                            <q-tr
                                :props="props"
                                v-show="props.expand"
                                v-for="(count, subCategory) in props.row.total"
                                :key="props.row.category + '-' + subCategory"
                            >
                                <q-td>
                                    <span class="pl-4 text-nowrap"
                                        >{{ subCategory }}
                                    </span>
                                </q-td>
                                <q-td class="text-center">{{ count }}</q-td>
                            </q-tr>
                        </template>
                    </QTable>
                </div>
                <div>
                    <div v-if="grandTotal > 0" class="header pt-3">
                        <div class="col-6 p-0"></div>
                        <div class="col-6 p-0 search-option">
                            <p style="color: #ffffff;">.</p>
                            <input
                                v-if="!isLoadingDetails"
                                v-model="filterItem"
                                class="form-control col-md-6"
                                name="title"
                                placeholder="Search by name"
                            />
                            <i
                                class="fas fa-times"
                                v-if="filterItem"
                                @click="filterItem = ''"
                            ></i>
                        </div>
                    </div>
                    <QTable
                        class="sticky-table"
                        style="overscroll-behavior: contain;"
                        :data="results"
                        :columns="formattedColumns"
                        :hide-bottom="true"
                        :rows-per-page-options="[0]"
                        :visible-columns="visibleColumns"
                        virtual-scroll
                        row-key="patient_id"
                        :filter="filterItem"
                        :filter-method="filteringResults"
                    >
                        <template v-slot:header="props">
                            <q-tr :props="props">
                                <q-th
                                    v-for="col in props.cols"
                                    :key="col.name"
                                    :props="props"
                                    class="sticky-header"
                                >
                                    <strong>{{ col.label }}</strong>
                                </q-th>
                            </q-tr>
                            <q-linear-progress
                                v-show="isLoadingDetails"
                                class="sticky-loader"
                                indeterminate
                                color="dark"
                            />
                        </template>
                        <template
                            v-slot:bottom-row
                            v-if="
                                results.length == 0 &&
                                !isLoadingDetails &&
                                !isError
                            "
                        >
                            <q-tr>
                                <q-td v-if="isRecordSharing" colspan="16"
                                    >No records available</q-td
                                >
                                <q-td v-else colspan="15"
                                    >No records available</q-td
                                >
                            </q-tr>
                        </template>
                        <template v-slot:body-cell-patient_name="props">
                            <q-td :props="props" class="">
                                <patient-name
                                    :use-router="USE_PATIENT_MODULE_VUE"
                                    :patient="generatePatientObject(props.row)"
                                    :show-status="true"
                                />
                            </q-td>
                        </template>
                        <template v-slot:body-cell-primary_owner="props">
                            <q-td :props="props" class="">
                                <client-name
                                    :use-router="USE_CLIENT_MODULE_VUE"
                                    :client="generateClientObject(props.row)"
                                />
                            </q-td>
                        </template>
                        <template v-slot:body-cell-homeLocation="props">
                            <q-td :props="props" class="">
                                <span v-if="props.row.primary_owner_id">
                                    {{ props.row.homeLocation }}
                                </span>
                            </q-td>
                        </template>
                    </QTable>
                </div>
            </div>
        </b-card>
        <FilterEngagedReport ref="filterEngagedReport" />
    </div>
</template>
<script>
import Report from "@/mixins/report.js";
import "@/css/quasar-table.css";
import { QTable } from "quasar";
import { DateTime } from "luxon";
import ReportService from "@/services/report";
import CREPORT from "@/constants/report";
import ClientName from "@/components/commons/client-name";
import PatientName from "@/components/commons/patient-name";
import FilterEngagedReport from "@/module/report/module/filter-engaged-report";
import { forEach } from "lodash";

const TOTAL_ENGAGED_PATIENT = "Total of Engaged Patients";

export default {
    components: {
        QTable,
        ClientName,
        PatientName,
        FilterEngagedReport,
        ReportHeader: () => import("@/components/report/report-header")
    },
    mixins: [Report],
    props: {
        data: {
            type: Object
        }
    },
    data() {
        return {
            locale: "en-US",
            loaded: false,
            meta: {},
            page: 1,
            pagination: {},
            results: [],
            total: 0,
            isExpandAll: false,
            grandTotal: 0,
            otherSpeciesTotal: 0,
            code: "",
            dateRange: "",
            length: 0,
            isLoading: false,
            isLoadingDetails: false,
            filterItem: null,
            title: "Active Engaged Patients",
            abortController: [],
            indexAxios: 0,
            isError: false,
            isDestroyed: false,
            isRecordSharing: false,
            homeLocation: "",
            currencySymbol: "",
            params: {
                to: null,
                interval_month: 18,
                home_location_id: null
            },
            summaryColumns: [
                {
                    name: "category",
                    field: "category",
                    label: "Species",
                    align: "left",
                    style: "width: 500px;"
                },
                {
                    name: "total",
                    field: "total",
                    label: "Total",
                    align: "center"
                }
            ],
            summaryData: [],
            summaryResults: [],
            formattedColumns: [
                {
                    name: "patient_name",
                    label: "Name",
                    field: "patient_name",
                    align: "left",
                    style: "min-width: 150px; vertical-align: top"
                },
                {
                    name: "id",
                    label: "ID",
                    field: "id",
                    align: "left",
                    style: "min-width: 110px; vertical-align: top"
                },
                {
                    name: "species",
                    label: "Species",
                    field: "species",
                    align: "left",
                    style: "min-width: 110px; vertical-align: top",
                    classes: "text-capitalize"
                },
                {
                    name: "breed",
                    label: "Breed",
                    field: "breed",
                    align: "left",
                    style: "min-width: 110px; vertical-align: top"
                },
                {
                    name: "sex",
                    label: "Sex",
                    field: "sex",
                    align: "left",
                    style: "min-width: 110px; vertical-align: top"
                },
                {
                    name: "age",
                    label: "Age",
                    field: "age",
                    align: "left",
                    style: "min-width: 110px; vertical-align: top",
                    classes: "text-capitalize"
                },
                {
                    name: "birthdate",
                    label: "Birthdate",
                    field: "birthdate",
                    align: "left",
                    style: "min-width: 110px; vertical-align: top"
                },
                {
                    name: "color",
                    label: "Color",
                    field: "color",
                    align: "left",
                    style: "min-width: 110px; vertical-align: top",
                    classes: "text-capitalize"
                },
                {
                    name: "chip",
                    label: "Chip",
                    field: "chip",
                    align: "left",
                    style: "min-width: 110px; vertical-align: top",
                    classes: "text-capitalize"
                },
                {
                    name: "primary_owner",
                    label: "Primary Owner",
                    field: "primary_owner",
                    align: "left",
                    style: "min-width: 150px; vertical-align: top"
                },
                {
                    name: "patient_status",
                    label: "Status",
                    field: "patient_status",
                    align: "left",
                    style: "min-width: 80px; vertical-align: top",
                    classes: "text-capitalize"
                },
                {
                    name: "home_location",
                    field: "home_location",
                    label: "Home Location",
                    align: "left",
                    style: "min-width: 150px; vertical-align: top"
                },
                {
                    name: "total_appointment",
                    field: "total_appointment",
                    label: "Total Appointment",
                    align: "left",
                    style: "min-width: 150px; vertical-align: top"
                },
                {
                    name: "last_appointment_date",
                    field: "last_appointment_date",
                    label: "Last Appointment Date",
                    align: "left",
                    style: "min-width: 150px; vertical-align: top"
                },
                {
                    name: "total_invoice_amount",
                    field: "total_invoice_amount",
                    label: "Total Invoice Amount",
                    align: "left",
                    style: "min-width: 150px; vertical-align: top"
                },
                {
                    name: "last_invoice_date",
                    field: "last_invoice_date",
                    label: "Last Invoice Date",
                    align: "left",
                    style: "min-width: 150px; vertical-align: top"
                }
            ]
        };
    },
    computed: {
        visibleColumns() {
            let self = this;
            // display home_location column only if clinic is record sharing
            let cols = self.formattedColumns.map(e => e.field);
            const index = cols.findIndex(e => e == "home_location");

            if (!self.isRecordSharing) cols.splice(index, 1);
            return cols;
        }
    },
    async mounted() {
        let self = this;

        self.data.filterDateOption = false;

        self.$refs.filterEngagedReport.open();

        self.$root.$on("active_engaged_patient:refresh", self.handleOnRefresh);

        self.$root.$on(
            "active_engaged_patient:load",
            self.handleOnFilterChanged
        );

        self.toggleGreyOutButton(true);
    },
    beforeDestroy: function () {
        let self = this;
        self.data.filterDateOption = true;
        self.data.isDestroyed = true;
        self.resetPagination();
        self.$root.$off("active_engaged_patient:refresh");
        if (self.abortController.length > 0) {
            self.abortController[self.indexAxios].abort();
        }
        self.toggleGreyOutButton(false);
    },
    methods: {
        init(isRefresh = false, resetResult = false, indexAxios) {
            let self = this;

            clearInterval(self.pagination.interval);
            self.loadingPercentages();
            self.abortController[indexAxios] = new AbortController();

            ReportService.getReport(CREPORT.TYPE.ACTIVE_ENGAGED_PATIENT, {
                ...self.params,
                refresh: isRefresh ? 1 : 0,
                show_results: false,
                signal: self.abortController[indexAxios].signal
            })
                .then(response => {
                    const reportResponse = response.resource;
                    const grandTotal = reportResponse.results.grandTotal;
                    self.grandTotal = grandTotal;

                    if (response.message && response.message.code === 200) {
                        self.loaded = true;
                        self.title = reportResponse.meta.title;
                        self.dynamicPageTitle(self.title);
                        self.meta = {
                            ...reportResponse.meta,
                            ...reportResponse.paginate
                        };

                        if (resetResult) {
                            self.results = [];
                        }

                        if (grandTotal == 0) {
                            self.toggleGreyOutButton(true);
                            self.isLoading = false;
                            self.isLoadingDetails = false;
                            self.loaded = true;
                        } else {
                            if (grandTotal > 0) {
                                self.otherSpeciesTotal =
                                    reportResponse.results.otherSpeciesTotal;
                                self.summaryData =
                                    reportResponse.results.summary_data;
                                self.summaryResults = self.getSummaryResults(
                                    self.summaryData
                                );
                                self.isLoading = false;
                                self.loaded = true;
                                self.continueFetch(
                                    self.params,
                                    isRefresh,
                                    indexAxios
                                );
                            }
                        }
                    } else {
                        throw new Error("Error: Unable to get the report.");
                    }
                })
                .catch(e => {
                    if (!self.abortController[indexAxios].signal.aborted) {
                        console.error(e);
                        self.isError = true;
                        self.isLoading = false;
                        self._message(
                            "Error: Unable to get the report",
                            "danger"
                        );
                    }
                });
        },
        async continueFetch(params, isRefresh, indexAxios) {
            let self = this;

            await ReportService.getReport(CREPORT.TYPE.ACTIVE_ENGAGED_PATIENT, {
                ...self.params,
                refresh: isRefresh ? 1 : 0,
                page: self.pagination.page,
                show_results: true,
                signal: self.abortController[indexAxios].signal
            })
                .then(response => {
                    const reportResponse = response.resource;
                    self.isRecordSharing = reportResponse.isRecordSharing;
                    self.currencySymbol = reportResponse.currencySymbol;

                    if (response.message && response.message.code === 200) {
                        reportResponse.results.forEach(e => {
                            const patient = {
                                id: e.ID,
                                patient_encoded_id: e.patient_encoded_id,
                                patient_name: e.patient_name
                            };

                            self.results.push({
                                index: e.ID,
                                id: e.ID,
                                patient_name: e.patient_name,
                                patient_encoded_id: e.patient_encoded_id,
                                species: e.species,
                                breed: e.breed,
                                sex: e.sex,
                                age: e.age,
                                birthdate: e.birthdate,
                                color: e.color,
                                chip: e.chip,
                                primary_owner: e.primary_owner,
                                primary_owner_id: e.primary_owner_id,
                                primary_owner_encoded_id:
                                    e.primary_owner_encoded_id,
                                home_location: e.home_location,
                                patient_status: e.patient_status,
                                total_appointment: e.total_appointment,
                                last_appointment_date: e.last_appointment_date,
                                total_invoice_amount:
                                    self.currencySymbol + e.total_sales,
                                last_invoice_date: e.last_invoice_date,
                                patient
                            });
                        });
                        self.pagination.lastPage =
                            reportResponse.paginate.lastPage;
                        self.length = self.results.length;

                        if (self.pagination.page < self.pagination.lastPage) {
                            self.pagination.page++;
                            self.continueFetch(params, isRefresh, indexAxios);
                        } else {
                            self.loadingCompleted();
                            self.toggleGreyOutButton(false);
                            self.isLoadingDetails = false;
                        }
                    } else {
                        throw new Error(
                            "Error: Unable to continue get the report",
                            "danger"
                        );
                    }
                })
                .catch(e => {
                    if (!self.abortController[indexAxios].signal.aborted) {
                        console.error(e);
                        self.isError = true;
                        self.isLoading = false;
                        self.isLoadingDetails = false;
                        self.results = [];
                        self._message(
                            "Error: Unable to continue get the report",
                            "danger"
                        );
                    }
                });
        },
        getSummaryResults(totalCategory) {
            let self = this;
            let msg = TOTAL_ENGAGED_PATIENT;
            if (self.homeLocation != "") {
                msg = msg + ` from ${self.homeLocation}:`;
            } else {
                msg = msg + ":";
            }

            let summaryResults = [];

            forEach(totalCategory, function (val, key) {
                summaryResults.push({
                    category: key,
                    total: val
                });
            });
            summaryResults.sort((a, b) => a.category.localeCompare(b.category));

            summaryResults.push({
                category: msg,
                total: self.grandTotal,
                class: "font-weight-bold"
            });

            return summaryResults;
        },

        generatePatientObject(row) {
            return {
                name: this.capitalize(row.patient_name),
                encodedId: row.patient_encoded_id,
                isactive: true
            };
        },
        generateClientObject(row) {
            const isDeleted = this.capitalize(row.primary_owner).includes(
                "(deleted)"
            );
            return {
                id: isDeleted ? null : row.primary_owner_id,
                name: this.capitalize(
                    row.primary_owner.replace("(deleted)", "")
                ),
                encoded_id: isDeleted ? null : row.primary_owner_encoded_id,
                isactive: !isDeleted
            };
        },
        toggleGreyOutButton(value) {
            let self = this;
            // to grey out the action menu when there is no data / loading
            self.data.isGreyOutButton = value;
        },
        filteringResults(rows) {
            let self = this;
            return rows.filter(row => {
                return row.patient_name
                    ? row.patient_name
                          .toLowerCase()
                          .includes(self.filterItem.toLowerCase())
                    : false;
            });
        },
        handleOnRefresh() {
            this.isLoading = true;
            this.isLoadingDetails = true;
            this.pagination = this.DEFAULT_PAGINATION();
            this.length = 0;
            this.page = 1;
            this.grandTotal = 0;
            this.summaryResults = [];
            this.results = [];
            if (this.abortController.length > 0) {
                this.abortController[this.indexAxios].abort();
            }
            this.indexAxios++;
            this.toggleGreyOutButton(true);
            this.init(true, true, this.indexAxios);
        },
        handleOnFilterChanged(params) {
            this.isLoading = true;
            this.isLoadingDetails = true;
            this.loaded = true;
            this.pagination = this.DEFAULT_PAGINATION();
            this.length = 0;
            this.grandTotal = 0;
            this.summaryResults = [];
            this.results = [];
            this.homeLocation = params.home_location?.name ?? "";
            this.toggleGreyOutButton(true);

            this.params = {
                to: params.to,
                interval_month: params.interval_month,
                home_location_id: params.home_location_id
            };

            const from = DateTime.fromSeconds(params.from);
            const to = DateTime.fromSeconds(params.to);

            // set selected date range
            this.dateRange = `(${from.toFormat(
                this._dateFormat
            )} - ${to.toFormat(this._dateFormat)})`;

            this.init(false, true, self.indexAxios);
        },
        handleOpenFilter() {
            this.$refs.filterEngagedReport.open();
        },
        getClientURI(id) {
            return process.env.CI_APPS + "client/client/view/" + id + "/0";
        },
        shouldExpand(row) {
            let self = this;

            let totalEngagedPatientsHomeLocation =
                TOTAL_ENGAGED_PATIENT + ` from ${self.homeLocation}:`;
            const excludeCategories = [
                "Equine",
                "Feline",
                "Canine",
                TOTAL_ENGAGED_PATIENT + ":",
                totalEngagedPatientsHomeLocation
            ];
            return !excludeCategories.includes(row.category);
        },
        toggleExpandAll(props) {
            this.isExpandAll = !this.isExpandAll;
            props.expand = this.isExpandAll;
        }
    }
};
</script>
<style scoped>
.summary-table tr td:first-child {
    width: 1%;
    white-space: nowrap;
}

.summary-table tr td {
    font-size: 1.125rem;
    font-weight: 700;
}
</style>
